import { inject, Injectable } from '@angular/core';
import {
  Consumer,
  ConsumerService,
  Employee,
  EmployeeService,
  Order,
  OrderResponse,
  OrderReview,
  OrderReviewService,
  OrderService,
} from '@app/resources/services';
import { catchError, Observable, of } from 'rxjs';
import { map, finalize } from 'rxjs/operators';
import { mapToOrder, mapToOrders } from '@app/areas/transactions/utils';
import { mapToConsumer, mapToOrderReview } from '@app/areas/members/utils';
import { mapToEmployee } from '@app/areas/admin/submodules/employees/utils';

@Injectable()
export class TransactionApiService {
  private readonly consumerService = inject(ConsumerService);
  private readonly employeeService = inject(EmployeeService);
  private readonly orderService = inject(OrderService);
  private readonly orderReviewService = inject(OrderReviewService);
  private abortController: AbortController | null = null;

  getTransactions(pageNumber: number, pageSize: number): Observable<{ orders: Order[]; totalTransactions: number }> {
    return this.orderService.findAll(pageNumber, pageSize).pipe(
      map((data) => ({
        orders: mapToOrders(data),
        totalTransactions: data.totalTransactions,
      }))
    );
  }

  getTransaction(orderId: number): Observable<Order> {
    return this.orderService.findUnique(orderId).pipe(
      map(mapToOrder),
      catchError((err) => {
        console.error(err);
        return of(err);
      })
    );
  }

  getTransactionsWithReviews(
    pageNumber: number,
    pageSize: number,
    consumerId: number
  ): Observable<{ orders: Order[]; totalTransactions: number }> {
    return this.orderService.findAllWithReviews(pageNumber, pageSize, consumerId).pipe(
      map((data) => ({
        orders: mapToOrders(data),
        totalTransactions: data.totalTransactions,
      }))
    );
  }

  getConsumerForTransaction(consumerId: number): Observable<Consumer> {
    return this.consumerService.findUnique(consumerId).pipe(map(mapToConsumer));
  }

  getReviewForTransaction(orderReviewId: string): Observable<OrderReview> {
    return this.orderReviewService.findUnique(orderReviewId).pipe(map(mapToOrderReview));
  }

  getEmployeeForTransaction(employeeId: number): Observable<Employee> {
    return this.employeeService.findUnique(employeeId).pipe(map(mapToEmployee));
  }

  getEmployeesForTransactions(): Observable<Employee[]> {
    return this.employeeService
      .findAll()
      .pipe(map((response) => (response ?? { $values: [] }).$values.map(mapToEmployee)));
  }

  searchTransactions(
    pageNumber: number,
    pageSize: number,
    query: string
  ): Observable<{ orders: Order[]; totalTransactions: number }> {
    // Abort any pending search request
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Create new controller for this request
    this.abortController = new AbortController();

    return this.orderService.findByOrderGuid(pageNumber, pageSize, query, this.abortController.signal).pipe(
      map((data) => ({
        orders: mapToOrders(data),
        totalTransactions: data.totalTransactions,
      })),
      finalize(() => {
        // Clean up the controller after the request completes or errors
        this.abortController = null;
      })
    );
  }

  getNonMemberForTransaction(consumerId: number): Observable<Consumer> {
    return this.consumerService.findNonMember(consumerId).pipe(map(mapToConsumer));
  }
}
